import { Acl } from '@contember/schema';
export const emptyRole = {
    variables: {},
    entities: {},
    stages: '*',
};
/**
 * Update the ACL of the schema. It needs to be used within `updateSchema`.
 *
 * @example
 * updateSchema(
 *   updateAcl(({ acl }) => {
 *     const newAcl = { ...acl }
 *     return newAcl
 *   })
 * )
 *
 */
export const updateAcl = (updater) => {
    return args => {
        return { ...args.schema, acl: updater({ schema: args.schema, acl: args.schema.acl }) };
    };
};
/**
 * Update the permissions of every role. It needs to be used within `updateAcl`
 *
 * @example
 * updateSchema(
 *   updateAcl(
 *     updateAclEveryRole(({ role }) => {
 *       const newRole = { ...role }
 *       return newRole
 *     })
 *   )
 * )
 */
export const updateAclEveryRole = (...updater) => {
    return args => ({
        ...args.acl,
        roles: Object.fromEntries(updater.reduce((acc, updater) => acc.map(([name, role]) => [name, updater({ ...args, role })]), Object.entries(args.acl.roles))),
    });
};
/**
 * Update a single role. It needs to be used within `updateAclRole`
 *
 * @example
 * updateSchema(
 *   updateAcl(
 *     updateAclRole(({ role, roleName }) => {
 *       if (roleName === 'admin') return role
 *
 *       const newRole = { ...role }
 *       return newRole
 *     })
 *   )
 * )
 */
export const updateAclRole = (updater) => {
    return args => ({
        ...args.acl,
        roles: Object.fromEntries(Object.entries(args.acl.roles).map(([name, role]) => [name, updater({ ...args, role, roleName: name })])),
    });
};
export const updateAclEntities = (updater) => {
    return args => ({
        ...args.role,
        entities: updater({ ...args, entities: args.role.entities }),
    });
};
export const updateAclEveryEntity = (...updater) => {
    return args => ({
        ...args.role,
        entities: Object.fromEntries(updater.reduce((acc, updater) => acc.map(([entity, entityPermissions]) => [
            entity,
            updater({
                ...args,
                entities: args.role.entities,
                entityName: entity,
                entityPermissions,
            }),
        ]), Object.entries(args.role.entities))),
    });
};
export const updateAclFieldPermissions = (updater) => {
    return ({ entityPermissions, entityName }) => {
        const operations = {};
        const handlers = {
            create: value => updater(value, entityName, Acl.Operation.create),
            update: value => updater(value, entityName, Acl.Operation.update),
            read: value => updater(value, entityName, Acl.Operation.read),
            delete: value => value,
            customPrimary: value => value,
        };
        const types = ['create', 'update', 'read', 'delete', 'customPrimary'];
        for (const key of types) {
            if (key in entityPermissions.operations) {
                operations[key] = handlers[key](entityPermissions.operations[key]);
            }
        }
        return {
            ...entityPermissions,
            operations,
        };
    };
};
export const updateAclEveryPredicate = (updater) => {
    return args => {
        return {
            ...args.entityPermissions,
            predicates: Object.fromEntries(Object.entries(args.entityPermissions.predicates).map(([name, predicate]) => [
                name,
                updater({
                    ...args,
                    predicate,
                }),
            ])),
        };
    };
};
//# sourceMappingURL=schemaAclUpdateUtils.js.map